import React, { useCallback, useEffect, useRef } from 'react';
import cx from 'classnames';

import { MobilizeRecommendationRow } from 'Components/VehicleList/MobilizeRecommendationRow';
import NoSale from 'Components/VehicleList/NoSale/NoSale';
import { useSeenFilterAutoLoad } from 'Components/VehicleList/useSeenFilterAutoLoad';
import { useHasFilters } from 'Components/VehicleList/VehicleListV2/FilterBarV2/hooks/useHasFilters';
import { AiCompanionContent } from 'Features/AiCompanion';
import { useAiCompanionExperimentation } from 'Services/optimizely/experimentations/useAiCompanionExperimentation';
import { useFilters, useHasUserSetSort } from 'Stores/FilterStore/VehicleListFilter/VehicleListFilterStore';
import { useListType } from 'Stores/ListStore/ListStore';
import type { Pagination } from 'Types/pagination';
import type { VehicleListItem } from 'Types/vehicle';
import { LIST_TYPES } from 'Utilities/consts';
import useIntersectionObserver from 'Utilities/hooks/useIntersectionObserver';
import useSaleTimes from 'Utilities/hooks/useSaleTimes';
import useShouldFetchVehicles from 'Utilities/hooks/vehicleList/useShouldFetchVehicles';

import { AiCompanionChip } from './FilterBarV2/FilterBarStart/AiCompanionChip';
import { getActiveFilterCount } from './FilterBarV2/FilterBarV2.helpers';
import { VehicleCardV2 } from './VehicleCardV2/VehicleCardV2';
import { VehicleCardV2Skeleton } from './VehicleCardV2/VehicleCardV2Skeleton';
import { VehicleGridV2 } from './VehicleGridV2/VehicleGridV2';
import { ConversationChip } from './ConversationChip';
import { FilterBarV2 } from './FilterBarV2';
import { SaleHeaderV2 } from './SaleHeaderV2';
import { VehicleListEmptyStateV2 } from './VehicleListEmptyStateV2';
import { VehicleListErrorV2 } from './VehicleListErrorV2';
import { VehicleListRefreshButtonV2 } from './VehicleListRefreshButtonV2';

import styles from './VehicleListV2.module.scss';

const SKELETON_COUNT = 12;

interface VehicleListV2Props {
	isError?: boolean;
	isLoading?: boolean;
	onLoadMore?: () => void;
	onRefresh: () => void;
	pagination: Pagination;
	vehicleCount: number;
	vehicles: VehicleListItem[];
}

export const VehicleListV2: React.FC<VehicleListV2Props> = ({
	isError,
	isLoading,
	onLoadMore,
	onRefresh,
	pagination,
	vehicleCount,
	vehicles,
}) => {
	const sentinelRef = useRef<HTMLDivElement>(null);
	const hasMoreRef = useRef(pagination?.hasMore);
	const isLoadingRef = useRef(isLoading);
	const listType = useListType();
	const isAiCompanion = listType === LIST_TYPES.aiCompanion;
	const isAiCompanionEnabled = useAiCompanionExperimentation();
	const showAiCompanion = isAiCompanion && isAiCompanionEnabled;
	const shouldFetchVehicles = useShouldFetchVehicles();
	const { isLiveSaleEnabled, isSaleActive } = useSaleTimes();
	const isLiveSaleActive = isLiveSaleEnabled && isSaleActive;
	const filters = useFilters();
	const appliedFiltersCount = getActiveFilterCount(filters);
	const hasUserSetSort = useHasUserSetSort();
	const hasActiveFilters = useHasFilters();

	const { filteredVehicles } = useSeenFilterAutoLoad({
		pagination,
		vehicleCount,
		vehicles,
	});

	useEffect(() => {
		hasMoreRef.current = pagination?.hasMore;
		isLoadingRef.current = isLoading;
	}, [pagination?.hasMore, isLoading]);

	const onIntersect = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			if (isLoadingRef.current || hasMoreRef.current === false) {
				return;
			}

			const entry = entries[0];
			if (!entry) return;

			if (entry.isIntersecting && entry.intersectionRatio === 1) {
				onLoadMore?.();
			}
		},
		[onLoadMore],
	);

	useIntersectionObserver({
		onIntersect,
		root: undefined,
		rootMargin: '0px 0px 1344px 0px',
		target: sentinelRef,
		threshold: 1,
	});

	if (showAiCompanion) {
		return (
			<div
				key="ai-companion"
				className={cx(styles.container, styles.aiCompanionContainer)}
				data-testid="vehicle-list-v2"
			>
				<div className={styles.aiCompanionBar}>
					<AiCompanionChip />
					<ConversationChip />
				</div>
				{isLiveSaleActive ? <AiCompanionContent hideMobileButton /> : <NoSale />}
			</div>
		);
	}

	if (isError) {
		return <VehicleListErrorV2 />;
	}

	const isSaleLiveAndHasVehicles = shouldFetchVehicles && !!vehicleCount;
	const isSaleLiveNoVehiclesHasActiveFilters = shouldFetchVehicles && !vehicleCount && hasActiveFilters;
	const isSaleLiveAndLoading = shouldFetchVehicles && !vehicleCount && isLoading;

	const showFilters = isSaleLiveAndHasVehicles || isSaleLiveNoVehiclesHasActiveFilters || isSaleLiveAndLoading;

	return (
		<div key="vehicle-list" className={cx(styles.container, styles.fadeIn)} data-testid="vehicle-list-v2">
			{showFilters && <FilterBarV2 isLoading={isLoading} />}
			<SaleHeaderV2 vehicleCount={vehicleCount} />
			<div className={styles.mobilizeWrapper}>
				<MobilizeRecommendationRow
					appliedFiltersCount={appliedFiltersCount}
					hasUserSetSort={hasUserSetSort}
					listType={listType}
				/>
			</div>
			<VehicleListEmptyStateV2
				filteredVehicleCount={filteredVehicles.length}
				isLoading={isLoading}
				vehicleCount={vehicleCount}
			/>
			{shouldFetchVehicles && filteredVehicles.length > 0 && (
				<VehicleGridV2>
					<VehicleListRefreshButtonV2 hasVehicles={filteredVehicles.length > 0} onRefresh={onRefresh} />
					{filteredVehicles.map((vehicle) => (
						<VehicleCardV2 key={vehicle.id} vehicle={vehicle} />
					))}
					{isLoading &&
						Array.from({ length: SKELETON_COUNT }, (_, i) => <VehicleCardV2Skeleton key={`skeleton-${i}`} />)}
				</VehicleGridV2>
			)}
			{shouldFetchVehicles && filteredVehicles.length === 0 && isLoading && (
				<VehicleGridV2>
					{Array.from({ length: SKELETON_COUNT }, (_, i) => (
						<VehicleCardV2Skeleton key={`skeleton-${i}`} />
					))}
				</VehicleGridV2>
			)}
			<div ref={sentinelRef} className={styles.sentinel} />
		</div>
	);
};
